import { SxProps } from "@mui/material";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { gql } from "generated-graphql";
import { MultipleTenantPickerQuery } from "generated-graphql/graphql";
import React, { forwardRef, useCallback } from "react";
import { ControllerFieldState } from "react-hook-form";

import { PickerInput, useMultiPickerWithHydration } from "../hooks/useMultiPickerWithHydration";
import AsyncAutocomplete from "./Forms/AsyncAutocomplete";

const TENANTS_QUERY = gql(`
  query MultipleTenantPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    tenants(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
      }
      count
    }
  }
`);

export type TenantOption = {
  id: string;
  name: string;
};

interface MultipleTenantPickerProps extends ControllerFieldState {
  onChange: (tenants: TenantOption[] | null) => void;
  defaultSearchTerm?: string;
  disabledTenantsById?: string[];
  disabledOptionLabel?: string;
  label?: string;
  value?: (TenantOption | PickerInput<TenantOption>)[] | null;
  lazyLoad?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  showErrors?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

export const MultipleTenantPicker: React.FC<MultipleTenantPickerProps> = forwardRef<
  HTMLDivElement,
  MultipleTenantPickerProps
>(function MultipleTenantPicker(
  {
    onChange,
    defaultSearchTerm = "",
    disabledTenantsById = [],
    disabledOptionLabel = "",
    label = "Tenant",
    value,
    error,
    disabled,
    showErrors = false,
    sx,
    onOpen,
    onClose,
  },
  ref
) {
  const getItems = useCallback((data: MultipleTenantPickerQuery) => data.tenants?.items ?? [], []);

  const { resolvedValue, isHydrating, enhancedOnChange, enhancedGetItems } =
    useMultiPickerWithHydration<TenantOption, MultipleTenantPickerQuery>({
      value: value || null,
      query: TENANTS_QUERY,
      getItems,
      idsSearchPrefix: "ids",
      onChange,
    });

  return (
    <>
      <AsyncAutocomplete
        ref={ref}
        query={TENANTS_QUERY}
        initialPageSize={20}
        variables={{}}
        getItems={enhancedGetItems}
        getOptionLabel={(tenant) => tenant.name ?? ""}
        placeholderLabel={label}
        onChange={enhancedOnChange}
        value={resolvedValue}
        error={error}
        disabled={disabled || isHydrating}
        additionalSearchString={defaultSearchTerm}
        multiple={true}
        filterSelectedOptions
        getOptionDisabled={(option: TenantOption) => disabledTenantsById.includes(option?.id ?? "")}
        loading={isHydrating}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.name}
            {disabledTenantsById.includes(option?.id ?? "") && (
              <span>&nbsp;({disabledOptionLabel})</span>
            )}
          </li>
        )}
        sx={sx}
        onOpen={onOpen}
        onClose={onClose}
      />
      {showErrors && <ErrorDisplay error={error} />}
    </>
  );
});
